import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarCheck, Home, Wallet, Settings,
  ShieldCheck, AlertCircle, Loader2, ExternalLink, MapPin, BedDouble,
  Sparkles, Phone, Mail,
} from "lucide-react";
import StripeConnectPanel from "@/components/StripeConnectPanel";

const TABS = [
  { k: "overview", l: "Vue d'ensemble", Icon: LayoutDashboard },
  { k: "bookings", l: "Réservations", Icon: CalendarCheck },
  { k: "listings", l: "Mes logements", Icon: Home },
  { k: "earnings", l: "Revenus", Icon: Wallet },
  { k: "settings", l: "Paramètres & Stripe", Icon: Settings },
];

export default function ConciergeDashboardPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (k) => setParams({ tab: k });
  const [partner, setPartner] = useState(undefined);  // undefined = loading, null = no profile

  useEffect(() => {
    if (!user) return;
    api.get("/me/concierge/profile")
      .then(({ data }) => setPartner(data))
      .catch(() => setPartner(null));
  }, [user]);

  if (!user) return null;
  if (partner === undefined) {
    return <div className="pt-32 text-center text-neutral-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Chargement…</div>;
  }
  if (partner === null) {
    return <NoProfileEmpty />;
  }

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Espace conciergerie</div>
          <h1 className="font-display text-xl font-bold leading-tight mb-1" data-testid="conc-partner-name">{partner.company_name}</h1>
          <div className="flex items-center gap-2 text-xs mb-6">
            <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold ${partner.status === "approved" ? "bg-[#166534] text-white" : "bg-[#C9A14A] text-[#0A0A0A]"}`}>
              {partner.status === "approved" ? "Approuvé MSB" : partner.status}
            </span>
            {partner.insurance && (
              <span className="text-[9px] uppercase tracking-[0.15em] font-bold bg-[#166534] text-white px-2 py-0.5 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Assuré
              </span>
            )}
          </div>
          <nav className="space-y-1">
            {TABS.map(({ k, l, Icon }) => (
              <button
                key={k}
                data-testid={`conc-nav-${k}`}
                onClick={() => setTab(k)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors border-l-2 ${
                  tab === k ? "bg-white border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-600 hover:text-[#0A0A0A]"
                }`}
              >
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </nav>
          <Link to="/partenaires" className="text-xs underline mt-6 inline-block text-neutral-500 hover:text-[#0A0A0A]">
            Voir ma fiche publique →
          </Link>
        </aside>

        <main className="min-w-0">
          {!user.stripe_charges_enabled && (
            <div className="mb-6 p-5 border-l-4 border-[#C9A14A] bg-[#FFFBEA]" data-testid="conc-stripe-warn">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#9A4D1F] shrink-0" />
                <div>
                  <div className="font-display text-lg font-bold">Connectez votre compte Stripe pour recevoir vos virements</div>
                  <p className="text-sm text-neutral-700 mt-1">Tant que Stripe n'est pas connecté, les propriétaires ne pourront pas vous lier à leurs annonces.</p>
                  <button onClick={() => setTab("settings")} className="mt-2 text-sm font-bold underline">Configurer Stripe →</button>
                </div>
              </div>
            </div>
          )}

          {tab === "overview" && <Overview partner={partner} />}
          {tab === "bookings" && <BookingsTab />}
          {tab === "listings" && <ListingsTab />}
          {tab === "earnings" && <EarningsTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

function NoProfileEmpty() {
  return (
    <div className="pt-32 min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 text-center py-16">
        <Sparkles className="w-12 h-12 mx-auto text-[#7A1535]" />
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-6">
          Pas encore de profil conciergerie
        </h1>
        <p className="text-neutral-600 mt-5 leading-relaxed">
          Cet espace est réservé aux conciergeries et prestataires ménage validés par MSB. Postulez en 5 minutes et notre équipe étudie votre dossier sous 5 jours.
        </p>
        <Link to="/devenir-partenaire" className="inline-flex items-center gap-2 mt-8 bg-[#0A0A0A] text-white px-7 py-3.5 font-semibold hover:bg-[#1B2A49]">
          Postuler maintenant <ExternalLink className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

/* ====================== OVERVIEW ====================== */
function Overview({ partner }) {
  const [earnings, setEarnings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get("/me/concierge/earnings").then(({ data }) => setEarnings(data));
    api.get("/me/concierge/bookings").then(({ data }) => setBookings(data));
    api.get("/me/concierge/listings").then(({ data }) => setListings(data));
  }, []);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return bookings.filter((b) => b.check_in >= today && (b.status === "confirmed" || b.status === "pending")).slice(0, 5);
  }, [bookings]);

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-8">Bonjour {partner.contact_name?.split(" ")[0]}</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard testid="conc-stat-listings" label="Logements affiliés" value={listings.length} sub="Propriétés gérées" />
        <StatCard testid="conc-stat-upcoming" label="Séjours à venir" value={earnings?.upcoming_bookings ?? "—"} sub={`${earnings?.upcoming_eur ?? "0"}€ à percevoir`} />
        <StatCard testid="conc-stat-revenue" label="Revenus encaissés" value={`${earnings?.total_eur ?? "—"}€`} sub={`${earnings?.paid_bookings ?? 0} séjours payés`} accent />
        <StatCard testid="conc-stat-status" label="Statut MSB" value={partner.status === "approved" ? "Actif" : "En validation"} sub={partner.insurance ? "Assurance OK" : "Renseignez votre RC"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Prochains séjours</div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6">Aucun séjour à venir. Vos prochaines réservations apparaîtront ici.</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map((b) => (
                <div key={b.id} className="border border-black/10 p-3 text-sm">
                  <div className="font-semibold">{b.listing_title}</div>
                  <div className="text-xs text-neutral-600 mt-1">{b.check_in} → {b.check_out} · {b.guests} voyageurs</div>
                  <div className="text-xs mt-2 flex items-center gap-2">
                    <span className="font-display text-base font-bold text-[#7A1535]">
                      {(b.pricing?.concierge_payout || 0).toFixed(2)}€
                    </span>
                    <span className="text-neutral-500 uppercase text-[10px] tracking-[0.18em] font-bold">{b.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-black/10 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Logements affiliés</div>
          {listings.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6">Aucun propriétaire ne vous a encore lié à son annonce. <Link to="/partenaires" className="underline">Vérifiez votre fiche publique</Link>.</p>
          ) : (
            <div className="space-y-2">
              {listings.map((l) => (
                <Link key={l.id} to={`/listings/${l.id}`} className="block border border-black/10 p-3 hover:border-[#0A0A0A] text-sm">
                  <div className="font-semibold">{l.title}</div>
                  <div className="text-xs text-neutral-600 mt-1">{l.city} · {l.region} · {l.concierge_revenue_share_percent || 0}% sur nuitées</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className={`p-5 border ${accent ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10"}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accent ? "text-white/70" : "text-neutral-500"}`}>{label}</div>
      <div className="font-display text-3xl font-bold mt-2 leading-none">{value}</div>
      <div className={`text-xs mt-2 ${accent ? "text-white/70" : "text-neutral-600"}`}>{sub}</div>
    </div>
  );
}

/* ====================== BOOKINGS ====================== */
function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");

  useEffect(() => {
    setLoading(true);
    api.get("/me/concierge/bookings").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const filtered = items.filter((b) => {
    if (filter === "upcoming") return b.check_in >= today && (b.status === "confirmed" || b.status === "pending");
    if (filter === "past") return b.check_in < today;
    return true;
  });

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Réservations</h2>
      <div className="flex gap-2 mb-4">
        {[{ v: "upcoming", l: "À venir" }, { v: "past", l: "Passées" }, { v: "all", l: "Toutes" }].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border ${filter === f.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>{f.l}</button>
        ))}
      </div>
      {loading ? <Loader /> : filtered.length === 0 ? (
        <div className="bg-white border border-black/10 p-8 text-center text-neutral-500 text-sm">Aucune réservation dans cette vue</div>
      ) : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {filtered.map((b) => (
            <div key={b.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`conc-bk-${b.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-semibold">{b.listing_title}</div>
                <div className="text-xs text-neutral-600 mt-1 flex items-center gap-3 flex-wrap">
                  <span><CalendarCheck className="w-3 h-3 inline mr-1" /> {b.check_in} → {b.check_out}</span>
                  <span>{b.nights} nuit{b.nights > 1 ? "s" : ""}</span>
                  <span>{b.guests} voyageur{b.guests > 1 ? "s" : ""}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1">{b.guest_name} · {b.guest_email}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl font-bold text-[#7A1535]">{(b.pricing?.concierge_payout || 0).toFixed(2)}€</div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold mt-1">{b.status}</div>
                <div className="text-[10px] text-neutral-500 mt-1">{b.payment_status === "paid" ? "Payé · transfert lancé" : "En attente paiement"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====================== LISTINGS ====================== */
function ListingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/me/concierge/listings").then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Logements gérés</h2>
      {loading ? <Loader /> : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-black/15 p-10 text-center">
          <p className="text-neutral-600 mb-3">Aucun propriétaire ne vous a encore confié d'annonce.</p>
          <p className="text-sm text-neutral-500">Les hôtes vous découvrent via <Link to="/partenaires" className="underline">l'annuaire</Link>. Pensez à enrichir votre fiche pour maximiser vos chances.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((l) => (
            <div key={l.id} className="border border-black/10 bg-white p-5" data-testid={`conc-lst-${l.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <Link to={`/listings/${l.id}`} className="font-display text-lg font-bold hover:underline">{l.title}</Link>
                  <div className="text-xs text-neutral-600 mt-1 flex items-center gap-2"><MapPin className="w-3 h-3" /> {l.city}, {l.region}</div>
                </div>
                <div className="text-xs text-neutral-500 flex items-center gap-1"><BedDouble className="w-3 h-3" /> {l.bedrooms}</div>
              </div>
              <div className="border-t border-black/5 mt-3 pt-3 text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">Votre part</span><strong className="text-[#7A1535]">{l.concierge_revenue_share_percent || 0}% nuitée</strong></div>
                {l.concierge_gets_cleaning_fee && (
                  <div className="flex justify-between mt-1"><span className="text-neutral-500">Frais de ménage</span><strong>{l.cleaning_fee || 0}€ pour vous</strong></div>
                )}
              </div>
              {l.owner && (
                <div className="border-t border-black/5 mt-3 pt-3 text-xs text-neutral-600">
                  <div className="font-bold mb-1">Hôte : {l.owner.first_name}</div>
                  <div className="flex items-center gap-3">
                    {l.owner.email && <a href={`mailto:${l.owner.email}`} className="hover:underline flex items-center gap-1"><Mail className="w-3 h-3" /> Email</a>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====================== EARNINGS ====================== */
function EarningsTab() {
  const [earnings, setEarnings] = useState(null);
  useEffect(() => { api.get("/me/concierge/earnings").then(({ data }) => setEarnings(data)); }, []);
  if (!earnings) return <Loader />;
  const months = Object.entries(earnings.by_month || {}).sort((a, b) => b[0].localeCompare(a[0]));
  const max = Math.max(...months.map(([, v]) => v), 1);

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Revenus</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard testid="conc-earn-total" label="Total encaissé" value={`${earnings.total_eur}€`} sub={`${earnings.paid_bookings} séjours`} accent />
        <StatCard testid="conc-earn-upcoming" label="À encaisser" value={`${earnings.upcoming_eur}€`} sub={`${earnings.upcoming_bookings} séjours à venir`} />
        <StatCard testid="conc-earn-month" label="Ce mois-ci" value={`${earnings.by_month[new Date().toISOString().slice(0, 7)] || 0}€`} sub="Versements automatiques Stripe" />
      </div>
      <div className="bg-white border border-black/10 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-4">Historique mensuel</div>
        {months.length === 0 ? (
          <p className="text-sm text-neutral-500">Aucun revenu enregistré pour l'instant.</p>
        ) : (
          <div className="space-y-3">
            {months.map(([m, v]) => (
              <div key={m} className="flex items-center gap-4">
                <div className="w-20 text-xs font-bold uppercase tracking-[0.18em]">{m}</div>
                <div className="flex-1 h-2 bg-neutral-100 relative">
                  <div className="absolute inset-y-0 left-0 bg-[#7A1535]" style={{ width: `${(v / max) * 100}%` }} />
                </div>
                <div className="w-24 text-right font-display text-base font-bold">{v.toFixed(2)}€</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-neutral-500 mt-6">
        💡 Vous pouvez consulter le détail de vos virements et télécharger vos factures directement depuis votre tableau de bord Stripe (onglet <strong>Paramètres & Stripe</strong>).
      </p>
    </div>
  );
}

/* ====================== SETTINGS ====================== */
function SettingsTab() {
  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Paramètres de paiement</h2>
      <StripeConnectPanel />
      <div className="mt-8 p-5 border border-black/10 bg-white text-sm space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Modifier votre fiche publique</div>
        <p className="text-neutral-600">Pour modifier les informations affichées dans l'annuaire (description, langues, services, photo), contactez notre équipe au <a href="tel:+33972158090" className="font-bold underline">09 72 15 80 90</a> ou par email à <a href="mailto:partenaires@monsejourenbretagne.fr" className="font-bold underline">partenaires@monsejourenbretagne.fr</a>.</p>
      </div>
    </div>
  );
}

function Loader() { return <div className="py-12 text-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>; }
